import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Dashboard from './components/Dashboard';
import Pricing from './components/Pricing';
import PaymentSuccess from './components/PaymentSuccess';
import MaintenanceHistory from './components/MaintenanceHistory';
import RepairHistory from './components/RepairHistory';
import PartsInventory from './components/PartsInventory';
import ExpiredSubscriptionModal from './components/ExpiredSubscriptionModal';
import { LanguageProvider } from './contexts/LanguageContext';
import '@/App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios interceptor for auth
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    } else if (error.response?.status === 403) {
      // Handle subscription expired or access denied
      const message = error.response?.data?.detail || '';
      if (message.includes('subscription has expired')) {
        // Trigger subscription check which will show modal
        window.dispatchEvent(new CustomEvent('checkSubscription'));
        return Promise.reject(error);
      } else if (message.includes('trial has expired') || message.includes('Access denied')) {
        // Show toast but don't redirect - let user navigate freely
        toast.error('Access denied. Please subscribe or renew to continue.', {
          duration: 5000,
          action: {
            label: 'Subscribe',
            onClick: () => window.location.href = '/pricing'
          }
        });
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showExpiredModal, setShowExpiredModal] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        // Check subscription status
        checkSubscriptionStatus(token);
      } catch (error) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);

    // Listen for user profile updates
    const handleUserUpdate = (event) => {
      if (event.detail && event.detail.user) {
        setUser(event.detail.user);
      }
    };

    // Listen for subscription updates (after payment)
    const handleSubscriptionUpdate = () => {
      const token = localStorage.getItem('token');
      if (token) {
        checkSubscriptionStatus(token);
      }
    };

    window.addEventListener('userUpdated', handleUserUpdate);
    window.addEventListener('subscriptionUpdated', handleSubscriptionUpdate);
    
    return () => {
      window.removeEventListener('userUpdated', handleUserUpdate);
      window.removeEventListener('subscriptionUpdated', handleSubscriptionUpdate);
    };
  }, []);

  const checkSubscriptionStatus = async (token) => {
    try {
      const response = await fetch(`${API}/subscription/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const status = await response.json();
        setSubscriptionStatus(status);
        
        // Show expired modal if subscription is expired and user is not on pricing page
        if (status.is_expired && !window.location.pathname.includes('/pricing')) {
          setShowExpiredModal(true);
        }
      }
    } catch (error) {
      console.error('Failed to check subscription status:', error);
    }
  };

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    toast.success(`Welcome back, ${userData.username}!`);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#E1F4FA]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <LanguageProvider>
      <div className="App min-h-screen bg-[#E1F4FA]">
        <Router>
          <Routes>
          <Route 
            path="/login" 
            element={
              !user ? (
                <Login onLogin={handleLogin} />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route 
            path="/pricing" 
            element={
              <div className="min-h-screen bg-[#E1F4FA]">
                <Pricing />
              </div>
            } 
          />
          <Route 
            path="/payment-success" 
            element={
              user ? (
                <div className="min-h-screen bg-[#E1F4FA]">
                  <PaymentSuccess />
                </div>
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/*" 
            element={
              user ? (
                <Dashboard user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
        </Routes>
      </Router>
      <Toaster position="top-right" richColors />
      {/* Show expired subscription modal when needed */}
      {showExpiredModal && subscriptionStatus?.is_expired && (
        <ExpiredSubscriptionModal 
          subscriptionStatus={subscriptionStatus}
          onClose={() => setShowExpiredModal(false)}
        />
      )}
    </div>
    </LanguageProvider>
  );
}

export default App;
